class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  connectedCallback() {
    this.onHistoryRestore = (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      const navigation = performance.getEntriesByType('navigation')[0];
      if (event.persisted || navigation?.type === 'back_forward') this.refreshAfterHistoryRestore();
    };
    window.addEventListener('pageshow', this.onHistoryRestore);
    // A fresh customer action must take precedence over an earlier read.
    this.cancelHistoryRefresh = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.historyRefresh?.abort());
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.onHistoryRestore);
    this.cancelHistoryRefresh?.();
    this.historyRefresh?.abort();
  }

  async refreshAfterHistoryRestore() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    this.historyRefresh?.abort();
    const controller = new AbortController();
    this.historyRefresh = controller;
    const timeout = setTimeout(() => controller.abort(), 8000);

    try {
      // Read-only section rendering: no cart mutation or commerce event.
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart sections unavailable');
      const sections = await response.json();
      if (controller.signal.aborted || !this.isConnected || !window.matchMedia('(max-width: 749px)').matches) return;
      if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) return;

      const parser = new DOMParser();
      const drawerPage = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const sourceDrawer = drawerPage.querySelector('cart-drawer');
      const sourceContent = drawerPage.querySelector('#CartDrawer');
      const sourceBubble = parser.parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      const content = this.querySelector('#CartDrawer');
      const bubble = document.getElementById('cart-icon-bubble');
      if (!sourceDrawer || !sourceContent || !sourceBubble || !content || !bubble) return;

      const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;
      content.innerHTML = sourceContent.innerHTML;
      this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
      bubble.innerHTML = sourceBubble.innerHTML;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

      if (this.classList.contains('active')) {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') : content;
        const focusTarget = focusedId && document.getElementById(focusedId);
        trapFocus(container, focusTarget || this.querySelector('.drawer__close'));
      }
    } catch (error) {
      // Keep the existing cart usable if the refresh fails or a new action wins.
      if (error.name !== 'AbortError') console.warn('MIAMUR: cart refresh unavailable after history navigation.');
    } finally {
      clearTimeout(timeout);
      if (this.historyRefresh === controller) this.historyRefresh = null;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

